import { Group, rem, Text, useComputedColorScheme } from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { useCallback, useState, type FC } from "react";
import { useTRPC } from "../../../trpc";
import { FormattedMessage } from "react-intl";
import { useSetUser, useUser } from "../../../hooks/user";
import { useQuery } from "@tanstack/react-query";
import type { UserDetails } from "@project/api";

interface ChangeAvatarDropzoneProps {
  user: UserDetails;
}

export const ChangeAvatarDropzone: FC<ChangeAvatarDropzoneProps> = () => {
  const setUser = useSetUser();
  const user = useUser();
  const trpc = useTRPC();

  const [isUploading, setIsUploading] = useState(false);

  const { refetch } = useQuery(
    trpc.getCurrentUser.queryOptions(undefined, {
      enabled: false,
    }),
  );
  const computedColorScheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  const bg = computedColorScheme === "dark"
    ? "dark.6"
    : "gray.1";

  const handleAvatarChange = useCallback(([file]: File[]) => {
    if (!file) {
      return;
    }
    const form = new FormData();
    form.append("file", file);

    setIsUploading(true);
    fetch(`/api/users/${user?.id}/avatars`,
      {
        method: "POST",
        body: form,
      })
      .then((response: Response) => {
        if (!response.ok) {
          logger.error({
            status: response.status,
            statusText: response.statusText,
          }, "Failed to upload avatar");
          setIsUploading(false);
        }
        else {
          void refetch().then(({ data }) => {
            if (data) {
              setUser(data);
            }
            setIsUploading(false);
          });
        }
      })
      .catch((error: Error) => {
        setIsUploading(false);
        logger.error({
          error,
        }, "Failed to upload avatar");
      });
  }, [setIsUploading]);

  return (
    <Dropzone
      loading={isUploading}
      onDrop={handleAvatarChange}
      maxSize={5 * 1024 ** 2}
      accept={IMAGE_MIME_TYPE}
      multiple={false}
      m="-xl"
      bg={bg}
      style={{ border: "none" }}
    >
      <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: "none" }}>
        <Dropzone.Accept>
          <IconUpload
            style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-blue-6)" }}
            stroke={1.5}
          />

        </Dropzone.Accept>
        <Dropzone.Reject>
          <IconX
            style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-red-6)" }}
            stroke={1.5}
          />
        </Dropzone.Reject>
        <Dropzone.Idle>
          <IconPhoto
            style={{ width: rem(52), height: rem(52), color: "var(--mantine-color-dimmed)" }}
            stroke={1.5}
          />
        </Dropzone.Idle>

        <div>
          <Text size="xl" inline>
            <FormattedMessage defaultMessage="Drag images here or click" id="text.dragOrClick" />
          </Text>
          <Text size="sm" c="dimmed" inline mt={7}>
            <FormattedMessage defaultMessage="Attached file should not exceed 5mb" id="text.attachFiles" />
          </Text>
        </div>
      </Group>
    </Dropzone>
  );
};
